import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, Award, CheckCircle2, ChevronRight, FileSpreadsheet,
  PlayCircle, Quote, Sparkles, Star, Target, Building2, Zap
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import {
  company, heroStats, trades, services, whyChoose, process,
  testimonials, tools, blogPosts
} from '../mock';

function Counter({ value }) {
  // Parses numeric portion and suffix from strings like "99%", "24+", "1.2K", "48h"
  const match = String(value).match(/^([\d.]+)(.*)$/);
  const targetNum = match ? parseFloat(match[1]) : 0;
  const suffix = match ? match[2] : '';
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const hasDecimal = String(targetNum).includes('.');

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        const duration = 1200;
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - p, 3);
          setN(targetNum * eased);
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        obs.disconnect();
      }
    }, { threshold: 0.2 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [targetNum]);

  const display = hasDecimal ? n.toFixed(1) : Math.round(n).toString();
  return <span ref={ref}>{display}{suffix}</span>;
}

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section className="relative pt-32 lg:pt-44 pb-24 lg:pb-32 bg-[#0B1B33] overflow-hidden">
        {/* Grid bg */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-32 w-[500px] h-[500px] bg-amber-400/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Badge className="bg-amber-500/10 text-amber-400 border border-amber-400/20 hover:bg-amber-500/15 px-3 py-1.5 rounded-full text-xs font-medium uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> ASPE & AACE Aligned
            </Badge>
            <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight">
              Construction <span className="text-amber-400">Estimating</span><br />
              &amp; Material <span className="relative inline-block">
                Takeoffs
                <svg className="absolute -bottom-2 left-0 w-full" height="12" viewBox="0 0 200 12" fill="none">
                  <path d="M2 9 Q 100 -2, 198 8" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round" fill="none" />
                </svg>
              </span>
            </h1>

            {/* Speed promise — the headline-grabber */}
            <div className="mt-7 inline-flex items-center gap-3 bg-gradient-to-r from-amber-500/15 to-amber-400/5 border border-amber-400/30 rounded-2xl pl-2 pr-5 py-2">
              <div className="w-10 h-10 rounded-xl bg-amber-500 text-[#0B1B33] flex items-center justify-center font-bold shadow-lg shadow-amber-500/30">
                <Zap className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-amber-400 text-xs uppercase tracking-wider font-semibold">Industry's Fastest Turnaround</div>
                <div className="text-white text-base font-bold">24–48 Hours · Guaranteed</div>
              </div>
            </div>

            <p className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed">
              Senior estimators. ±2% accuracy. Industry-fastest <b className="text-amber-400">24–48 hour</b> turnaround.
              Core Estimation LLC is the outsourced preconstruction partner trusted by GCs, subs and developers across the US.
            </p>

            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-[#0B1B33] font-semibold h-12 px-7 rounded-xl shadow-lg shadow-amber-500/20">
                  Book an Appointment <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-amber-400 h-12 px-7 rounded-xl backdrop-blur">
                  <PlayCircle className="w-5 h-5 mr-2" /> Explore Services
                </Button>
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-6 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-amber-400" /> 24–48 hour delivery
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-amber-400" /> CSI MasterFormat
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-amber-400" /> Fixed-fee pricing
              </div>
            </div>
          </div>

          {/* Hero card */}
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-tr from-amber-400/40 to-transparent rounded-3xl blur-xl" />
              <Card className="relative bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-3xl p-6 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1772442198624-4fc4d7281e89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxjb25zdHJ1Y3Rpb24lMjBibHVlcHJpbnR8ZW58MHx8fHwxNzc4NTgxNTQyfDA&ixlib=rb-4.1.0&q=85"
                  alt="Construction estimator working on blueprints"
                  className="w-full h-72 object-cover rounded-2xl"
                />
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                    <FileSpreadsheet className="w-5 h-5 text-amber-400" />
                    <div className="mt-2 text-white text-sm font-semibold">Excel + PDF</div>
                    <div className="text-xs text-slate-400 mt-0.5">Deliverable</div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                    <Target className="w-5 h-5 text-amber-400" />
                    <div className="mt-2 text-white text-sm font-semibold">±2% Accuracy</div>
                    <div className="text-xs text-slate-400 mt-0.5">QA reviewed</div>
                  </div>
                </div>
                <div className="mt-3 bg-amber-500/10 border border-amber-400/20 rounded-xl p-4 flex items-center gap-3">
                  <Award className="w-8 h-8 text-amber-400 flex-shrink-0" />
                  <div>
                    <div className="text-white text-sm font-semibold">24–48 hour delivery on every job</div>
                    <div className="text-xs text-slate-300">The fastest senior-reviewed estimates in the industry.</div>
                  </div>
                </div>
              </Card>
              {/* Floating chip */}
              <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-2xl p-4 hidden md:flex items-center gap-3">
                <div className="flex -space-x-2">
                  {[1,2,3].map(i => (
                    <div key={i} className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 border-2 border-white" />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(i => <Star key={i} className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />)}
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">Rated 4.9 / 5 by 230+ clients</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="relative max-w-7xl mx-auto px-6 mt-20">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {heroStats.map((s, i) => (
              <div key={i} className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:border-amber-400/30 transition">
                <div className="text-4xl md:text-5xl font-bold text-amber-400 tracking-tight">
                  <Counter value={s.value} />
                </div>
                <div className="text-sm text-slate-300 mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPEED PROMISE — sales hook */}
      <section className="relative bg-amber-500 py-6 md:py-7 overflow-hidden">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'repeating-linear-gradient(135deg, rgba(11,27,51,0.15) 0 14px, transparent 14px 28px)' }} />
        <div className="relative max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-center md:justify-between gap-4 text-center md:text-left">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#0B1B33] flex items-center justify-center flex-shrink-0">
              <Zap className="w-6 h-6 text-amber-400" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-[#0B1B33] text-xl md:text-2xl font-bold tracking-tight leading-tight">
                Industry's Fastest Turnaround — Just 24 to 48 Hours.
              </div>
              <div className="text-[#0B1B33]/80 text-sm md:text-base mt-1">
                Send your drawings today · Get a senior-reviewed estimate by tomorrow morning.
              </div>
            </div>
          </div>
          <Link to="/contact">
            <Button size="lg" className="bg-[#0B1B33] hover:bg-[#11264a] text-amber-400 font-semibold h-12 px-7 rounded-xl shadow-lg whitespace-nowrap">
              Start in 60 seconds <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* TRADES */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <Badge className="bg-[#0B1B33] text-amber-400 px-3 py-1 hover:bg-[#0B1B33]">Who we serve</Badge>
              <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#0B1B33] tracking-tight leading-tight">
                Estimating partners for every trade and every phase.
              </h2>
            </div>
            <p className="text-slate-600 max-w-md">
              A precise, trustworthy construction estimate is the single most
              important document of any successful project. We build it for you, properly.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trades.map((t) => (
              <Card key={t.slug} className="group bg-white border-slate-100 rounded-2xl overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                <div className="relative h-44 overflow-hidden">
                  <img src={t.image} alt={t.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B33]/80 to-transparent" />
                  <div className="absolute top-4 left-4 w-11 h-11 rounded-xl bg-amber-500 flex items-center justify-center shadow-lg">
                    <t.icon className="w-5 h-5 text-[#0B1B33]" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-[#0B1B33] group-hover:text-amber-600 transition">{t.title}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{t.short}</p>
                  <Link to="/services" className="mt-4 inline-flex items-center text-sm font-semibold text-[#0B1B33] group-hover:text-amber-600 transition">
                    Learn more <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition" />
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 px-3 py-1">Why Core Estimation</Badge>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#0B1B33] tracking-tight leading-tight">
              Built around the way modern bid rooms actually work.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              We are detail-oriented, deadline-honoring, and obsessed with the small line items that
              decide whether you win a bid — and whether you make money on it.
            </p>
            <div className="mt-8 relative rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1574757987642-5755f0839101?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXJ8ZW58MHx8fHwxNzc4NTgxNTQyfDA&ixlib=rb-4.1.0&q=85"
                alt="Estimator reviewing plans"
                className="w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B33]/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <div className="text-xs uppercase tracking-wider text-amber-400 mb-1">Mission Statement</div>
                <div className="font-semibold">Estimates that win bids — and protect your margins after.</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {whyChoose.map((w, i) => (
              <Card key={i} className="group p-7 rounded-2xl border-slate-100 hover:border-amber-300 hover:shadow-lg transition-all bg-white">
                <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center group-hover:bg-amber-500 transition">
                  <w.icon className="w-5 h-5 text-amber-600 group-hover:text-[#0B1B33] transition" />
                </div>
                <h3 className="mt-5 font-bold text-[#0B1B33] text-lg">{w.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{w.text}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="py-24 bg-[#0B1B33] relative overflow-hidden">
        <div className="absolute -top-40 right-0 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <div>
              <Badge className="bg-white/10 text-amber-400 px-3 py-1 hover:bg-white/10">Services</Badge>
              <h2 className="mt-4 text-4xl md:text-5xl font-bold text-white tracking-tight leading-tight max-w-2xl">
                A complete preconstruction stack — one phone call.
              </h2>
            </div>
            <Link to="/services">
              <Button variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-amber-500 hover:text-[#0B1B33] hover:border-amber-500 rounded-xl h-11 px-5">
                View all services <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.slice(0, 9).map((s) => (
              <Link to={`/services/${s.slug}`} key={s.slug} className="group">
                <Card className="relative bg-white/[0.04] border border-white/10 rounded-2xl p-7 h-full hover:bg-white/[0.07] hover:border-amber-400/30 transition-all duration-300">
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                      <s.icon className="w-5 h-5 text-[#0B1B33]" />
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-slate-500 group-hover:text-amber-400 group-hover:rotate-12 transition" />
                  </div>
                  <div className="mt-5 text-xs text-amber-400 uppercase tracking-wider font-semibold">{s.category}</div>
                  <h3 className="mt-1 text-lg font-bold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.summary}</p>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-[#0B1B33] text-amber-400 px-3 py-1 hover:bg-[#0B1B33]">Our Process</Badge>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#0B1B33] tracking-tight">
              From plans to priced bid in six clean steps.
            </h2>
            <p className="mt-4 text-slate-600">
              No black-box pricing. You see scope, lead time and fee before we start work.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {process.map((p, i) => (
              <Card key={i} className="relative bg-white rounded-2xl border border-slate-100 p-7 hover:shadow-xl hover:-translate-y-1 transition-all">
                <div className="absolute top-4 right-4 text-5xl font-black text-slate-100">{p.step}</div>
                <div className="w-11 h-11 rounded-xl bg-[#0B1B33] text-amber-400 flex items-center justify-center font-bold relative z-10">
                  {p.step}
                </div>
                <h3 className="mt-5 text-lg font-bold text-[#0B1B33] relative z-10">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed relative z-10">{p.text}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 px-3 py-1">Client voices</Badge>
              <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#0B1B33] tracking-tight leading-tight">
                Trusted by 230+ contractors and developers.
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((t, i) => (
              <Card key={i} className="p-8 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-100 hover:shadow-xl transition">
                <Quote className="w-8 h-8 text-amber-400" />
                <p className="mt-4 text-slate-700 leading-relaxed text-[15px]">“{t.text}”</p>
                <div className="mt-6 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-[#0B1B33]">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                  <div className="flex gap-0.5">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-amber-500 text-amber-500" />
                    ))}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Tools marquee */}
          <div className="mt-20">
            <div className="text-center text-xs uppercase tracking-[0.2em] text-slate-500 mb-6">
              Software & cost data we use daily
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
              {tools.map((t, i) => (
                <div key={i} className="text-slate-400 hover:text-[#0B1B33] font-semibold text-lg transition cursor-default">
                  {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BLOG */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <div>
              <Badge className="bg-[#0B1B33] text-amber-400 px-3 py-1 hover:bg-[#0B1B33]">From our blog</Badge>
              <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#0B1B33] tracking-tight leading-tight">
                Insights from the estimating floor.
              </h2>
            </div>
            <Link to="/blog">
              <Button variant="outline" className="border-[#0B1B33] text-[#0B1B33] hover:bg-[#0B1B33] hover:text-white rounded-xl h-11 px-5">
                All articles <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogPosts.slice(0, 3).map((p) => (
              <Link to={`/blog/${p.slug}`} key={p.slug} className="group">
                <Card className="bg-white border-slate-100 rounded-2xl overflow-hidden h-full hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                  <div className="relative h-52 overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <Badge className="absolute top-4 left-4 bg-amber-500 text-[#0B1B33] hover:bg-amber-500 font-semibold">{p.category}</Badge>
                  </div>
                  <div className="p-6">
                    <div className="text-xs text-slate-500 mb-3">{p.date} · {p.readTime} read</div>
                    <h3 className="text-lg font-bold text-[#0B1B33] group-hover:text-amber-600 transition leading-snug">{p.title}</h3>
                    <p className="mt-3 text-sm text-slate-600 line-clamp-2">{p.excerpt}</p>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative bg-[#0B1B33] rounded-3xl overflow-hidden p-10 md:p-16">
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-amber-500/20 rounded-full blur-3xl" />
            <div
              className="absolute inset-0 opacity-[0.05]"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
                backgroundSize: '40px 40px',
              }}
            />
            <div className="relative grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <Building2 className="w-10 h-10 text-amber-400" />
                <h2 className="mt-4 text-3xl md:text-5xl font-bold text-white tracking-tight leading-tight">
                  Got drawings? Let us put a real number on them.
                </h2>
                <p className="mt-5 text-slate-300 max-w-xl">
                  We respond to every quote request within 4 business hours with scope confirmation,
                  lead time, and a fixed fee — no obligation.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
                <Link to="/contact">
                  <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-[#0B1B33] font-semibold h-13 px-7 rounded-xl shadow-lg shadow-amber-500/30 w-full">
                    Request a Quote <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <a href={`tel:${company.phone}`}>
                  <Button size="lg" variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white hover:text-[#0B1B33] h-13 px-7 rounded-xl w-full">
                    Call {company.phone}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
